import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import QuestionMarkOutline from '@monofront/design-system/visuals/Icons/QuestionMarkOutline';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import {
  ACTIONS,
  RegisterMachineEvents,
} from '@monofront/business/stateMachines/register';
import { useEditorialSidePanelContext } from '@monofront/business/contexts';
import { useHouseholdSize } from '@hooks/useHouseholdSize';
import { useTranslations } from 'next-intl';
import { format } from '@monofront/business/utils';
import { useAverageProductBasket } from '@hooks/useAverageProductBasket';
import { FilledImageFieldImage } from '@prismicio/client';
import { ActorRef } from 'xstate';

type DefaultContentStepSliceProps = {
  prismicContent: {
    slice_type: string;
    variation: string;
    primary: {
      image?: FilledImageFieldImage;
      title: string;
      text: string;
      cta_label: string;
      tooltip_link?: { uid: string };
    };
  };
  props?: { actor?: ActorRef<any, RegisterMachineEvents> };
};

type DefaultContentStepSlice = DefaultContentStepSliceProps & {
  withSavingsTextImage: boolean;
};

const DefaultContentStepSlice: React.FC<DefaultContentStepSlice> = ({
  prismicContent,
  props,
  withSavingsTextImage,
}) => {
  const { setEditorialPanelSourceUid } = useEditorialSidePanelContext();
  const { size } = useHouseholdSize();
  const tFlow = useTranslations('SubscriptionFunnel.Flow');
  const { image, title, text, cta_label, tooltip_link } =
    prismicContent.primary;
  const { savings } = useAverageProductBasket();

  return (
    <section
      data-slice-type={prismicContent.slice_type}
      data-slice-variation={prismicContent.variation}
      className="tw-flex tw-flex-1 tw-pt-8 desktop:tw-py-9"
    >
      <div className="tw-mx-auto tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-between desktop:tw-justify-start">
        <div className="tw-mb-10 tw-flex tw-max-w-[600px] tw-flex-col tw-items-center tw-px-2 desktop:tw-px-12">
          {image && (
            <div className="tw-relative tw-mb-10 tw-max-w-[320px] desktop:tw-max-w-[420px]">
              <ResponsiveImage
                width={image.dimensions?.width}
                height={image.dimensions?.height}
                src={image.url}
                alt={image.alt ?? ''}
              />
              {withSavingsTextImage && (
                <div className="tw-absolute tw-inset-0 tw-flex tw-items-start tw-justify-center tw-pt-[33%] desktop:tw-pt-[38%]">
                  <h2 className="tw-flex tw-flex-col tw-items-center tw-text-center">
                    <span className="tw-lf-title tw-text-8xl">
                      {format(Math.abs(savings * 12), {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                    <span className="tw-lf-title tw-text-[40px]">
                      {tFlow('SavingsCalculatorStep.annually')}
                    </span>
                  </h2>
                </div>
              )}
            </div>
          )}
          <h2 className="tw-lf-title tw-lf-title-sm tw-mb-2 tw-text-center tw-leading-[130%] desktop:tw-lf-title-lg">
            {title}
          </h2>
          <span className="tw-inline tw-text-center tw-leading-[150%]">
            {text?.replace(
              /%HOUSEHOLD_SIZE%/g,
              tFlow('HouseholdSizeSelectorStep.people', { size }),
            )}
            {tooltip_link && (
              <button
                type="button"
                className="tw-lf-button-ghost tw-z-30 tw-ml-2 tw-w-4 tw-cursor-pointer tw-transition tw-duration-300 tw-ease-in-out hover:tw-opacity-60"
                onClick={() => setEditorialPanelSourceUid(tooltip_link.uid)}
              >
                <span className="tooltip-icon tw-text-black-1000 [&_svg]:tw-align-top">
                  <QuestionMarkOutline size={IconSizes.sm} aria-hidden />
                </span>
              </button>
            )}
          </span>
        </div>
        <div className="tw-fixed tw-bottom-0 tw-w-full tw-px-3 tw-pb-3 desktop:tw-relative desktop:tw-py-0">
          <button
            className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-w-full desktop:tw-lf-button-lg desktop:tw-mx-auto desktop:tw-w-fit"
            onClick={() => {
              props?.actor?.send({ type: ACTIONS.NEXT });
            }}
            data-testid="ContentStepSlice-Button-next"
          >
            {cta_label}
          </button>
        </div>
      </div>
    </section>
  );
};

const VariationDefault: React.FC<DefaultContentStepSliceProps> = ({
  prismicContent,
  props,
}) => (
  <DefaultContentStepSlice
    prismicContent={prismicContent}
    props={props}
    withSavingsTextImage={false}
  />
);

const VariationWithSavingsTextImage: React.FC<DefaultContentStepSliceProps> = ({
  prismicContent,
  props,
}) => (
  <DefaultContentStepSlice
    prismicContent={prismicContent}
    props={props}
    withSavingsTextImage
  />
);

const ContentStepSlice: React.FC<DefaultContentStepSliceProps> = ({
  prismicContent,
  props,
  ...rest
}) => {
  switch (prismicContent.variation) {
    case 'withSavingsTextImage':
      return (
        <VariationWithSavingsTextImage
          prismicContent={prismicContent}
          props={props}
          {...rest}
        />
      );
    default:
      return (
        <VariationDefault
          prismicContent={prismicContent}
          props={props}
          {...rest}
        />
      );
  }
};

export default ContentStepSlice;
